"use client"

import React, { useCallback, useState } from "react"
import clsx from "clsx"
import { Box } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Checkbox from "ui/components/atoms/checkbox"
import { ErrorMessage } from "ui/components/atoms/input"
import { Label } from "ui/components/atoms/typography"
import { BaseStyles } from "ui/types/base-styles"

import styles from "./CheckboxesGroup.module.css"

export type RadioCheckboxesProps = BaseStyles & {
  name: string
  className?: string
  value: any[]
  options: number[][] | string[][]
  label?: string
  labelIcon?: string
  seeMore?: number
  seeMoreLabel?: string
  seeLessLabel?: string
  onChange?: (e: { name: string; value: any }) => void
  error?: {
    type?: string
    message?: string
  }
  $type?: "primary" | "secondary"
  $iconColor?: string
}

const CheckboxesGroup = React.forwardRef<HTMLDivElement, RadioCheckboxesProps>(
  function CheckboxesGroup(
    {
      name,
      className,
      value = [],
      options = [],
      label,
      error,
      labelIcon,
      onChange = () => {},
      seeMore,
      seeMoreLabel,
      seeLessLabel,
      $type = "primary",
      $iconColor,
      ...rest
    },
    ref,
  ) {
    const [seeAll, setSeeAll] = useState<boolean | null>(seeMore ? false : null)

    const handleOnChange = useCallback(
      (e: any[]) => {
        onChange({
          name,
          value: e,
        })
      },
      [onChange, name],
    )

    const handleClick = useCallback(
      ({ name, value: elValue }: { name: string; value: boolean }) => {
        if (elValue) {
          const arr = [...value, name]
          handleOnChange(arr)
        } else {
          handleOnChange(value.filter(e => e !== name))
        }
      },
      [handleOnChange, value],
    )

    const checkboxesContentClasses = clsx(styles.checkboxesContent, {
      [styles.secondary]: $type === "secondary",
    })

    return (
      <Box ref={ref} className={clsx(styles.wrapper, className)} {...rest}>
        {label && (
          <Label icon={labelIcon} $marginBottom="S">
            {label}
          </Label>
        )}
        <div className={checkboxesContentClasses}>
          {options
            .filter((e, index) => (seeMore && !seeAll ? index <= seeMore : true))
            .map((el: number[] | string[]) => (
              <Checkbox
                key={`${el[1]}`}
                name={`${el[1]}`}
                value={value?.includes(el[1])}
                onChange={handleClick}
                label={`${el[0]}`}
                icon={`${el[2]}`}
                $type={$type}
                $iconColor={$iconColor}
              />
            ))}
        </div>
        {seeAll !== null && (
          <Button
            icon={seeAll ? "minus" : "plus"}
            $marginLeft="auto"
            $marginRight="auto"
            $marginTop="S"
            onClick={() => setSeeAll(!seeAll)}
            $type="fourth"
            $padding="0"
          >
            {seeAll ? seeLessLabel : seeMoreLabel}
          </Button>
        )}
        {error && (
          <ErrorMessage $marginLeft="0" $marginTop="S">
            {error?.message ?? "To pole jest wymagane"}
          </ErrorMessage>
        )}
      </Box>
    )
  },
)

export default CheckboxesGroup
